import { createContext, useCallback, useContext, useReducer } from 'react';
import { useLocation } from 'react-router';

const MIN_WIDTH = 400;

type LayoutState = {
  panelWidth: number;
  isOpen: boolean;
};

interface AssistantLayoutContextType extends LayoutState {
  displayMode: 'page' | 'panel';
  open: () => void;
  close: () => void;
  setPanelWidth: (width: number) => void;
}

const STORAGE_KEY = 'DragonAssistantLayout';

const AssistantLayoutContext = createContext<AssistantLayoutContextType | null>(null);

export function useDragonAssistantLayout() {
  const context = useContext(AssistantLayoutContext);
  if (!context) {
    throw new Error('useDragonAssistantLayout must be used within a DragonAssistantLayoutProvider');
  }
  return context;
}

const initialState: LayoutState = {
  panelWidth: 400,
  isOpen: false,
};

type LayoutAction =
  | {
      type: 'SET_PANEL_WIDTH';
      payload: number;
    }
  | {
      type: 'SET_OPEN_STATE';
      payload: boolean;
    };

const reducer = (state: LayoutState, action: LayoutAction) => {
  let newState = state;
  switch (action.type) {
    case 'SET_PANEL_WIDTH':
      {
        const newWidth = action.payload;
        const oldWidth = state.panelWidth;
        if (newWidth === oldWidth) return state;
        if (newWidth < MIN_WIDTH) newState = { ...state, isOpen: !state.isOpen, panelWidth: MIN_WIDTH };
        else newState = { ...state, panelWidth: newWidth };
      }
      break;
    case 'SET_OPEN_STATE':
      newState = { ...state, isOpen: action.payload };
      break;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
  return newState;
};

export function DragonAssistantLayoutProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const displayMode = location.pathname.includes('/dragon-assistant') ? 'page' : 'panel';
  const [state, dispatch] = useReducer(reducer, initialState, () => {
    const storedState = localStorage.getItem(STORAGE_KEY);
    return storedState ? JSON.parse(storedState) : initialState;
  });

  const open = useCallback(() => dispatch({ type: 'SET_OPEN_STATE', payload: true }), [dispatch]);
  const close = useCallback(() => dispatch({ type: 'SET_OPEN_STATE', payload: false }), [dispatch]);

  const setPanelWidth = useCallback(
    (width: number) => dispatch({ type: 'SET_PANEL_WIDTH', payload: width }),
    [dispatch]
  );

  return (
    <AssistantLayoutContext.Provider
      value={{
        panelWidth: state.panelWidth,
        isOpen: state.isOpen,
        displayMode,
        open,
        close,
        setPanelWidth,
      }}
    >
      {children}
    </AssistantLayoutContext.Provider>
  );
}
